'use client';

import { createContext, ReactNode, useCallback, useContext, useState } from 'react';

type ToastKind = 'success' | 'error' | 'info';

interface Toast {
  id: number;
  kind: ToastKind;
  message: string;
}

interface ToastContextValue {
  push: (kind: ToastKind, message: string) => void;
  success: (message: string) => void;
  error: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastContextValue | null>(null);

const KIND_CONFIG: Record<ToastKind, { icon: string; classes: string }> = {
  success: {
    icon: 'check_circle',
    classes: 'bg-surface-container-lowest text-on-surface border-emerald-500/60',
  },
  error: {
    icon: 'error',
    classes: 'bg-surface-container-lowest text-on-surface border-error/60',
  },
  info: {
    icon: 'info',
    classes: 'bg-surface-container-lowest text-on-surface border-primary/60',
  },
};

const ICON_COLOR: Record<ToastKind, string> = {
  success: 'text-emerald-500',
  error: 'text-error',
  info: 'text-primary',
};

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const push = useCallback((kind: ToastKind, message: string) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, kind, message }]);
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, 3200);
  }, []);

  const value: ToastContextValue = {
    push,
    success: (m) => push('success', m),
    error:   (m) => push('error', m),
    info:    (m) => push('info', m),
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed bottom-6 right-6 z-[100] flex flex-col gap-2 pointer-events-none">
        {toasts.map((t) => {
          const cfg = KIND_CONFIG[t.kind];
          return (
            <div
              key={t.id}
              className={`pointer-events-auto flex items-center gap-3 px-4 py-3 rounded-xl border shadow-xl min-w-[240px] max-w-sm backdrop-blur-xl animate-in slide-in-from-right-5 duration-200 ${cfg.classes}`}
              role="status"
            >
              <span className={`material-symbols-outlined ${ICON_COLOR[t.kind]}`} style={{ fontSize: '20px' }}>
                {cfg.icon}
              </span>
              <span className="text-sm font-medium flex-1">{t.message}</span>
              <button
                onClick={() => setToasts((prev) => prev.filter((x) => x.id !== t.id))}
                className="text-on-surface-variant hover:text-on-surface"
                aria-label="Dismiss"
              >
                <span className="material-symbols-outlined" style={{ fontSize: '16px' }}>close</span>
              </button>
            </div>
          );
        })}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    // Soft fallback — toasts are nice-to-have, shouldn't crash the page.
    return {
      push: () => {},
      success: () => {},
      error: () => {},
      info: () => {},
    };
  }
  return ctx;
}
